import * as Portal from '@radix-ui/react-portal';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import { getStudioUrl } from '@cartwhl/common/utils';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import Text from '@cartwhl/ui/base/Text';

import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import CrossIcon from '../icons/CrossIcon';
import Button from '../input/Button';
import './MobileMenu.css';

// Check if we're on the homepage origin (client-side nav works)
// vs another app like applets (need full page navigation)
const isOnHomepage = getHomepageUrl('/') === `${window.location.origin}/`;

function MobileMenuLink({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  if (isOnHomepage) {
    return (
      <Link to={to} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <a href={getHomepageUrl(to)} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

interface MenuItem {
  title: string;
  subtitle?: string;
  href: string;
  external?: boolean;
  comingSoonText?: string;
}

interface MobileMenuProps {
  productItems: MenuItem[];
  pricingItems: MenuItem[];
  learnItems?: MenuItem[];
  onComingSoonClick?: (slug?: string) => void;
}

type LinkLists = 'default' | 'products' | 'pricing' | 'learn';

export default function MobileMenu({
  productItems,
  pricingItems,
  learnItems,
  onComingSoonClick,
}: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showLinks, setShowLinks] = useState<LinkLists>('default');

  const { authStatus } = useSnapshot(authStore.state);
  const isSignedIn = authStatus === 'user';

  const openMenu = () => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };
  return (
    <>
      {/* Menu trigger button - only visible below lg breakpoint (1024px) */}
      <button
        onClick={openMenu}
        className="flex items-center lg:hidden"
        aria-label="Open menu"
      >
        <Text variant="web/body/medium/md">Menu</Text>
      </button>

      {isOpen && (
        <Portal.Root
          className="fixed top-0 right-0 left-0 z-100 bg-green-50/90 text-white backdrop-blur-[10px]"
          style={{ height: '100svh' }}
        >
          {/* Back button */}
          {showLinks !== 'default' && (
            <button
              onClick={() => setShowLinks('default')}
              className="absolute top-4 left-5 p-3"
            >
              <ChevronLeftIcon />
            </button>
          )}

          {/* Close button */}
          <button onClick={closeMenu} className="absolute top-4 right-5 p-3">
            <CrossIcon />
          </button>

          {showLinks === 'default' && (
            <div className="container flex flex-col py-4 pt-20 text-[32px]/[48px] font-medium">
              <div className="menu-item-animated">
                <button
                  onClick={() => setShowLinks('products')}
                  className="text-left"
                >
                  Product
                </button>
              </div>
              <MobileMenuLink
                to="/data"
                className="menu-item-animated"
                onClick={closeMenu}
              >
                Data
              </MobileMenuLink>
              {/* <MobileMenuLink
                className="menu-item-animated"
                to="/blog"
                onClick={closeMenu}
              >
                Blog
              </MobileMenuLink> */}
              <button
                onClick={() => setShowLinks('pricing')}
                className="menu-item-animated text-left"
              >
                Pricing
              </button>
              <MobileMenuLink
                className="menu-item-animated"
                to="/company"
                onClick={closeMenu}
              >
                Company
              </MobileMenuLink>
              {learnItems ? (
                <button
                  onClick={() => setShowLinks('learn')}
                  className="menu-item-animated text-left"
                >
                  Learn
                </button>
              ) : (
                <MobileMenuLink
                  className="menu-item-animated"
                  to="/docs"
                  onClick={closeMenu}
                >
                  Learn
                </MobileMenuLink>
              )}
              {isSignedIn ? (
                <MobileMenuLink
                  className="menu-item-animated text-gray-90"
                  to={getStudioUrl()}
                  onClick={closeMenu}
                >
                  Open Canvas
                </MobileMenuLink>
              ) : (
                <>
                  <MobileMenuLink
                    className="menu-item-animated text-gray-90"
                    to="/login"
                    onClick={closeMenu}
                  >
                    Sign in
                  </MobileMenuLink>
                  <MobileMenuLink
                    className="menu-item-animated mt-16"
                    to="/sign-up"
                    onClick={closeMenu}
                  >
                    <Button>Try it free</Button>
                  </MobileMenuLink>
                </>
              )}
            </div>
          )}

          {showLinks === 'products' && (
            <div className="container flex flex-col py-4 pt-20 text-[32px]/[48px] font-medium">
              {productItems.map((item) =>
                item.comingSoonText ? (
                  <button
                    key={item.href}
                    className="menu-item-animated flex items-center gap-3 text-left"
                    onClick={() => {
                      onComingSoonClick?.(item.href);
                      closeMenu();
                    }}
                  >
                    {item.title}
                    <span className="text-[14px]/[14px] text-white/60">
                      {item.comingSoonText}
                    </span>
                  </button>
                ) : (
                  <MobileMenuLink
                    to={item.href}
                    key={item.href}
                    className="menu-item-animated"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </MobileMenuLink>
                )
              )}
            </div>
          )}

          {showLinks === 'pricing' && (
            <div className="container flex flex-col py-4 pt-20 text-[32px]/[48px] font-medium">
              {pricingItems.map((item) => (
                <MobileMenuLink
                  to={item.href}
                  key={item.href}
                  className="menu-item-animated"
                  onClick={closeMenu}
                >
                  {item.title}
                </MobileMenuLink>
              ))}
            </div>
          )}

          {showLinks === 'learn' && learnItems && (
            <div className="container flex flex-col py-4 pt-20 text-[32px]/[48px] font-medium">
              {learnItems.map((item) =>
                item.external ? (
                  <a
                    href={item.href}
                    key={item.href}
                    className="menu-item-animated"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </a>
                ) : (
                  <MobileMenuLink
                    to={item.href}
                    key={item.href}
                    className="menu-item-animated"
                    onClick={closeMenu}
                  >
                    {item.title}
                  </MobileMenuLink>
                )
              )}
            </div>
          )}
        </Portal.Root>
      )}
    </>
  );
}
